'use client';

import { useRouter } from 'next/navigation';
import { useHotkeys } from 'react-hotkeys-hook';

import type { Post } from '@/features/blog/types/post';

/**
 * Ignore the shortcut when the user is typing, holding a modifier, or arrowing
 * through a focused horizontally-scrollable code block.
 */
function shouldIgnoreEvent(event: KeyboardEvent) {
  if (event.defaultPrevented) {
    return true;
  }

  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
    return true;
  }

  const target = event.target;
  if (target instanceof HTMLElement) {
    if (
      target.isContentEditable ||
      target.closest('input, textarea, select, [contenteditable]') !== null ||
      target.closest('pre') !== null
    ) {
      return true;
    }
  }

  return false;
}

export function PostKeyboardShortcuts({
  basePath,
  previous,
  next,
}: {
  basePath: string;
  previous: Post | null;
  next: Post | null;
}) {
  const router = useRouter();

  const navigate = (post: Post | null) => {
    if (post) {
      router.push(`${basePath}/${post.slug}`);
    }
  };

  useHotkeys('ArrowRight', (event) => {
    if (shouldIgnoreEvent(event)) {
      return;
    }

    navigate(next);
  });
  useHotkeys('ArrowLeft', (event) => {
    if (shouldIgnoreEvent(event)) {
      return;
    }

    navigate(previous);
  });

  return null;
}
